import type {
  EditableQuestion,
  EditableQuestionPart,
  EditableQuestionType,
  EditableGradingRubric,
  EditableRubricUnit,
  EditableVariable,
  EditorNormalizationResult,
  QuestionSectionKind,
  RawComponent,
  RawContentSection,
  RawQuestion,
  RawQuestionSection,
  RawSection,
  RawVariable,
} from './editorTypes';
import { extractPromptImages } from './promptContentUnits';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeString(entry))
    .filter((entry): entry is string => entry !== undefined);
}

function normalizeIdentifier(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function toSectionArray(value: unknown): RawSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSection[];
}

function toQuestionArray(value: unknown): RawQuestion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestion[];
}

function getQuestionSection(
  section: RawSection,
  kind: QuestionSectionKind,
): RawQuestionSection | null {
  const rawSection =
    kind === 'homework'
      ? section.question_section ?? section.questionSection
      : section.pre_lecture_question_section ?? section.preLectureQuestionSection;

  return isRecord(rawSection) ? (rawSection as RawQuestionSection) : null;
}

function getContentHtml(
  contentSection: RawContentSection | null | undefined,
  fallbackContent: unknown,
) {
  const rawContentUnits = contentSection?.content_units ?? contentSection?.contentUnits;
  const contentUnits = Array.isArray(rawContentUnits) ? rawContentUnits : [];

  for (const unit of contentUnits) {
    if (!isRecord(unit)) {
      continue;
    }

    const html = normalizeString(unit.text);

    if (html !== undefined) {
      return html;
    }
  }

  return normalizeString(fallbackContent) ?? '<p></p>';
}

function normalizeQuestionType(value: unknown): {
  type: EditableQuestionType;
  unsupportedType?: number | string;
} {
  if (typeof value === 'number') {
    if (value === 0 || value === 1) {
      return { type: 'MULTIPLE_CHOICE' };
    }

    if (value === 2) {
      return { type: 'SELECT_ALL' };
    }

    if (value === 3) {
      return { type: 'NUMERICAL_WITH_ERROR' };
    }

    if (value === 4) {
      return { type: 'EXACT_ANSWER' };
    }

    if (value === 5) {
      return { type: 'MULTI_PART' };
    }

    if (value === 6) {
      return { type: 'AI_UPLOAD' };
    }

    return {
      type: 'UNSUPPORTED',
      unsupportedType: value,
    };
  }

  const normalized = normalizeString(value)?.toUpperCase();

  if (!normalized || normalized === 'UNKNOWN' || normalized === 'MULTIPLE_CHOICE') {
    return { type: 'MULTIPLE_CHOICE' };
  }

  if (normalized === 'SELECT_ALL') {
    return { type: 'SELECT_ALL' };
  }

  if (normalized === 'NUMERICAL_WITH_ERROR') {
    return { type: 'NUMERICAL_WITH_ERROR' };
  }

  if (normalized === 'EXACT_ANSWER') {
    return { type: 'EXACT_ANSWER' };
  }

  if (normalized === 'MULTI_PART') {
    return { type: 'MULTI_PART' };
  }

  if (normalized === 'AI_UPLOAD') {
    return { type: 'AI_UPLOAD' };
  }

  return {
    type: 'UNSUPPORTED',
    unsupportedType: normalized,
  };
}

// Every marked option, for SELECT_ALL. getCorrectAnswerIndex below reads the
// first entry only, which is right for multiple choice and wrong here.
function getCorrectAnswerIndices(question: RawQuestion) {
  const value = question.answer_index ?? question.answerIndex;

  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) =>
      typeof entry === 'number' ? entry : normalizeNumber(entry),
    )
    .filter((entry): entry is number => entry !== undefined);
}

function getQuestionIdentity(question: RawQuestion, fallbackId: string) {
  return (
    normalizeIdentifier(question.uid) ??
    normalizeIdentifier(question.id) ??
    fallbackId
  );
}

function getCorrectAnswerIndex(question: RawQuestion) {
  const value = question.answer_index ?? question.answerIndex;

  if (!Array.isArray(value)) {
    return undefined;
  }

  const [firstValue] = value;
  return typeof firstValue === 'number' ? firstValue : normalizeNumber(firstValue);
}

function getQuestionOptions(question: RawQuestion) {
  const options = Array.isArray(question.options) ? question.options : [];

  if (options.length > 0) {
    return options.map((option) => option ?? '').map((option) => String(option));
  }

  const rawContentOptions = question.content_options ?? question.contentOptions;
  const contentOptions = Array.isArray(rawContentOptions) ? rawContentOptions : [];

  return contentOptions.map((contentOption, index) =>
    getContentHtml(contentOption, `Option ${index + 1}`),
  );
}

function normalizeVariables(rawVariables: RawVariable[] | null | undefined) {
  const variables = Array.isArray(rawVariables) ? rawVariables : [];

  return variables
    .filter((variable) => {
      const rawType = variable.type;
      const variableType =
        typeof rawType === 'number'
          ? rawType
          : normalizeString(rawType)?.toUpperCase();

      return variableType === 1 || variableType === '1' || variableType === 'NUMERICAL' || variableType === undefined;
    })
    .map((variable, index): EditableVariable => ({
      id:
        normalizeIdentifier(variable.name) ??
        `question-variable-${index + 1}`,
      maxValue: normalizeNumber(variable.max_value ?? variable.maxValue),
      minValue: normalizeNumber(variable.min_value ?? variable.minValue),
      name: normalizeString(variable.name) ?? `variable_${index + 1}`,
      type: 'NUMERICAL',
    }));
}

function normalizeRubricUnits(
  value: unknown,
  questionId: string,
): EditableRubricUnit[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(isRecord)
    .map((unit, index): EditableRubricUnit => ({
      details: normalizeString(unit.details) ?? '',
      id:
        normalizeIdentifier(unit.id) ??
        `${questionId}-rubric-unit-${index + 1}`,
      name: normalizeString(unit.name) ?? '',
      score: normalizeNumber(unit.score) ?? 0,
    }));
}

function normalizeGradingRubricForEditor(
  question: RawQuestion,
  questionId: string,
): EditableGradingRubric | undefined {
  const rawRubric = question.grading_rubric ?? question.gradingRubric;

  if (!isRecord(rawRubric)) {
    return undefined;
  }

  const units = normalizeRubricUnits(
    rawRubric.rubric_units ?? rawRubric.rubricUnits,
    questionId,
  );
  const totalScore =
    normalizeNumber(rawRubric.total_score ?? rawRubric.totalScore) ??
    units.reduce((total, unit) => total + unit.score, 0);

  return {
    rubricUnits: units.length > 0
      ? units
      : [
          {
            details: '',
            id: `${questionId}-rubric-unit-1`,
            name: 'Correctness',
            score: 1,
          },
        ],
    totalScore,
  };
}

export /**
 * Resolves the legacy mantissa/exponent split into the number it represents.
 *
 * Older content stored 7.92e10 as a value of 7.92 with an extra exponent of 10.
 * The editor shows and writes whole numbers, so the split is folded in on the
 * way in and dropped on the way out — one edit migrates the question, and
 * nothing writes the split again. Reading it wrong is worse than it sounds: the
 * serializer carries the stored question forward, so an unfolded exponent stays
 * attached to a value the instructor has since changed.
 */
function foldExtraExponent(
  value: number | undefined,
  rawExponent: unknown,
): number | undefined {
  if (value === undefined) {
    return undefined;
  }

  const exponent = normalizeNumber(rawExponent);

  if (exponent === undefined || exponent === 0) {
    return value;
  }

  return value * 10 ** exponent;
}

function normalizeQuestionPartForEditor(
  part: RawQuestion,
  parentId: string,
  partIndex: number,
): EditableQuestionPart {
  const questionType = normalizeQuestionType(part.type);
  const supportedType =
    questionType.type === 'MULTI_PART' || questionType.type === 'UNSUPPORTED'
      ? 'EXACT_ANSWER'
      : questionType.type;
  const gradingRubric = normalizeGradingRubricForEditor(part, `${parentId}-part-${partIndex + 1}`);

  return {
    answerUnits: normalizeString(part.answer_units ?? part.answerUnits),
    contentHtml: getContentHtml(
      (part.content_section ?? part.contentSection) as RawContentSection | undefined,
      part.content,
    ),
    correctAnswerIndex: getCorrectAnswerIndex(part),
    correctAnswerIndices: getCorrectAnswerIndices(part),
    detailedAnswerHtml: getContentHtml(part.detailed_answer ?? part.detailedAnswer, undefined),
    detailedAnswerImages: extractPromptImages(
      (part.detailed_answer ?? part.detailedAnswer) as RawContentSection | undefined,
      `${getQuestionIdentity(part, `${parentId}-part-${partIndex + 1}`)}-solution`,
    ),
    exactAnswer: normalizeString(part.exact_answer ?? part.exactAnswer),
    gradingRubric,
    hintHtml: getContentHtml(part.hint, undefined),
    hintImages: extractPromptImages(
      part.hint as RawContentSection | undefined,
      `${getQuestionIdentity(part, `${parentId}-part-${partIndex + 1}`)}-hint`,
    ),
    id: getQuestionIdentity(part, `${parentId}-part-${partIndex + 1}`),
    maxNumAttempts:
      normalizeNumber(part.max_num_attempts ?? part.maxNumAttempts) ??
      (supportedType === 'AI_UPLOAD' ? 1 : undefined),
    numericalAnswer: foldExtraExponent(
      normalizeNumber(part.numerical_answer ?? part.numericalAnswer),
      part.numerical_answer_extra_exponent ?? part.numericalAnswerExtraExponent,
    ),
    numericalError: normalizeNumber(part.numerical_error ?? part.numericalError),
    options: getQuestionOptions(part),
    originalContentHtml: getContentHtml(
      (part.content_section ?? part.contentSection) as RawContentSection | undefined,
      part.content,
    ),
    promptImages: extractPromptImages(
      (part.content_section ?? part.contentSection) as RawContentSection | undefined,
      getQuestionIdentity(part, `${parentId}-part-${partIndex + 1}`),
    ),
    rawOriginal: { ...part },
    type: supportedType,
    useCustomNumAttempts:
      normalizeBoolean(part.use_custom_num_attempts ?? part.useCustomNumAttempts) ??
      (supportedType === 'AI_UPLOAD' ? true : undefined),
    // Keep an explicit flag; infer true from the part's OWN authored variables
    // when the flag is absent (the serializer used to strip it, so stored parts
    // can carry variables without the flag — the next save then repairs the
    // data). Parts without their own variables stay undefined, NOT false:
    // multi-part variables are usually authored on the PARENT, and the student
    // normalizer inherits the parent's randomization only while the part has no
    // explicit flag of its own.
    useRandomVariables:
      supportedType === 'AI_UPLOAD'
        ? false
        : normalizeBoolean(
            part.use_random_variables ?? part.useRandomVariables,
          ) ??
          (Array.isArray(part.variables) && part.variables.length > 0
            ? true
            : undefined),
    value:
      normalizeNumber(part.value) ??
      (supportedType === 'AI_UPLOAD' ? gradingRubric?.totalScore : undefined),
    variableBasedAnswer: normalizeString(
      part.variable_based_answer ?? part.variableBasedAnswer,
    ),
  };
}

export function normalizeQuestionForEditor(
  question: RawQuestion,
  questionIndex: number,
): EditableQuestion {
  const typeDetails = normalizeQuestionType(question.type);
  const questionId = getQuestionIdentity(question, `editor-question-${questionIndex + 1}`);
  const contentHtml = getContentHtml(
    (question.content_section ?? question.contentSection) as RawContentSection | undefined,
    question.content,
  );
  const gradingRubric = normalizeGradingRubricForEditor(question, questionId);
  const rawParts = toQuestionArray(
    question.question_set?.questions ?? question.questionSet?.questions,
  );

  return {
    answerUnits: normalizeString(question.answer_units ?? question.answerUnits),
    authorUid: normalizeString(question.author_uid ?? question.authorUid),
    contentHtml,
    correctAnswerIndex: getCorrectAnswerIndex(question),
    correctAnswerIndices: getCorrectAnswerIndices(question),
    creditForParticipation:
      normalizeBoolean(
        question.credit_for_participation ?? question.creditForParticipation,
      ) ?? false,
    currentUserUid: normalizeString(
      question.current_user_uid ?? question.currentUserUid,
    ),
    detailedAnswerHtml: getContentHtml(
      question.detailed_answer ?? question.detailedAnswer,
      undefined,
    ),
    detailedAnswerImages: extractPromptImages(
      (question.detailed_answer ?? question.detailedAnswer) as
        | RawContentSection
        | undefined,
      `${questionId}-solution`,
    ),
    exactAnswer: normalizeString(question.exact_answer ?? question.exactAnswer),
    fullHash: normalizeNumber(question.full_hash ?? question.fullHash),
    gradingRubric,
    hintHtml: getContentHtml(question.hint, undefined),
    hintImages: extractPromptImages(
      question.hint as RawContentSection | undefined,
      `${questionId}-hint`,
    ),
    id: questionId,
    imagePaths: normalizeStringArray(question.image_paths ?? question.imagePaths),
    isNew: !normalizeIdentifier(question.uid),
    isPublic: normalizeBoolean(question.is_public ?? question.isPublic) ?? true,
    maxNumAttempts:
      normalizeNumber(question.max_num_attempts ?? question.maxNumAttempts) ??
      (typeDetails.type === 'AI_UPLOAD' ? 1 : undefined),
    numericalAnswer: foldExtraExponent(
      normalizeNumber(question.numerical_answer ?? question.numericalAnswer),
      question.numerical_answer_extra_exponent ??
        question.numericalAnswerExtraExponent,
    ),
    numericalError: normalizeNumber(
      question.numerical_error ?? question.numericalError,
    ),
    options: getQuestionOptions(question),
    originalContentHtml: contentHtml,
    parentSubject: normalizeString(
      question.parent_subject ?? question.parentSubject,
    ),
    parts:
      typeDetails.type === 'MULTI_PART'
        ? rawParts.map((part, partIndex) =>
            normalizeQuestionPartForEditor(part, questionId, partIndex),
          )
        : undefined,
    promptImages: extractPromptImages(
      (question.content_section ?? question.contentSection) as
        | RawContentSection
        | undefined,
      questionId,
    ),
    rawOriginal: { ...question },
    seedForRandomVariables: normalizeString(
      question.seed_for_random_variables ?? question.seedForRandomVariables,
    ),
    tags: Array.isArray(question.tags)
      ? question.tags.map((tag) => String(tag))
      : [],
    type: typeDetails.type,
    uid: normalizeIdentifier(question.uid),
    unsupportedType: typeDetails.unsupportedType,
    useCustomNumAttempts:
      normalizeBoolean(
        question.use_custom_num_attempts ?? question.useCustomNumAttempts,
      ) ?? false,
    useRandomVariables:
      normalizeBoolean(
        question.use_random_variables ?? question.useRandomVariables,
      ) ?? false,
    userFreeHash: normalizeNumber(
      question.user_free_hash ?? question.userFreeHash,
    ),
    value:
      normalizeNumber(question.value) ??
      (typeDetails.type === 'AI_UPLOAD' ? gradingRubric?.totalScore : undefined),
    variableBasedAnswer: normalizeString(
      question.variable_based_answer ?? question.variableBasedAnswer,
    ),
    variables: normalizeVariables(question.variables),
  };
}

function normalizeQuestionSectionState(
  component: RawComponent,
  kind: QuestionSectionKind,
) {
  const sections = toSectionArray(component.sections);

  for (const [sectionIndex, section] of sections.entries()) {
    const questionSection = getQuestionSection(section, kind);

    if (!questionSection) {
      continue;
    }

    const questions = toQuestionArray(questionSection.questions).map(
      (question, questionIndex) => normalizeQuestionForEditor(question, questionIndex),
    );
    const ownerSectionId =
      normalizeIdentifier(section.id) ??
      normalizeIdentifier(section.uid) ??
      `__generated-section-${sectionIndex + 1}`;

    return {
      ownerSectionId,
      questionSectionId: normalizeIdentifier(questionSection.id),
      questionSectionUid: normalizeNumber(questionSection.uid),
      questions,
    };
  }

  return {
    ownerSectionId: undefined,
    questionSectionId: undefined,
    questionSectionUid: undefined,
    questions: [] as EditableQuestion[],
  };
}

export function normalizeQuestionEditorState(
  component: RawComponent,
): Pick<
  EditorNormalizationResult,
  | 'preLectureQuestionSectionId'
  | 'preLectureQuestionSectionOwnerSectionId'
  | 'preLectureQuestionSectionUid'
  | 'preLectureQuestions'
  | 'questionSectionId'
  | 'questionSectionOwnerSectionId'
  | 'questionSectionUid'
  | 'questions'
> {
  const homework = normalizeQuestionSectionState(component, 'homework');
  const preLecture = normalizeQuestionSectionState(component, 'preLecture');

  return {
    preLectureQuestionSectionId: preLecture.questionSectionId,
    preLectureQuestionSectionOwnerSectionId: preLecture.ownerSectionId,
    preLectureQuestionSectionUid: preLecture.questionSectionUid,
    preLectureQuestions: preLecture.questions,
    questionSectionId: homework.questionSectionId,
    questionSectionOwnerSectionId: homework.ownerSectionId,
    questionSectionUid: homework.questionSectionUid,
    questions: homework.questions,
  };
}
